import { computed, ref, nextTick } from "vue";
import { useStore } from "vuex";
import Helpers from "../../helpers";
import { mensajeError } from "../../store/plataformaStore";

const filtrosResumenVacios = () => ({
    inmobiliaria_id: null,
    tipo: null,
    fecha_desde: null,
    fecha_hasta: null,
});

const filtrosListadoVacios = () => ({
    inmobiliaria_id: null,
    tipo: null,
    estado: null,
    modalidad: null,
    vigencia: null,
    q: null,
    fecha_desde: null,
    fecha_hasta: null,
    orden: null,
    direccion: null,
});

// Strings vacíos de los inputs también cuentan como sin filtro
const limpiar = (filtros) => {
    const limpios = {};
    Object.entries(filtros).forEach(([clave, valor]) => {
        limpios[clave] = valor === "" ? null : valor;
    });
    return Helpers.eliminarNull(limpios);
};

export function useNegociosPlataforma() {
    const store = useStore();

    const pestana = ref("reporte");
    const sinAcceso = ref(false);
    const alertTable = ref({ state: false, message: "", color: "success" });

    const filtrosResumen = ref(filtrosResumenVacios());
    const cargandoResumen = ref(true);

    const filtros = ref(filtrosListadoVacios());
    const spinner = ref(true);
    // El listado se pide la primera vez que se abre la pestaña
    let listadoCargado = false;

    // Detalle
    const modalDetalle = ref(false);
    const detalle = ref(null);
    const cargandoDetalle = ref(false);
    const descargando = ref(null);

    const showAlert = (message, color = "success", timeout = 5000) => {
        alertTable.value = { state: true, message, color };
        setTimeout(() => {
            alertTable.value.state = false;
        }, timeout);
    };

    const handleError = (error, porDefecto) => {
        if (error?.response?.status === 403) {
            sinAcceso.value = true;
            return;
        }
        showAlert(mensajeError(error, porDefecto), "danger");
        console.error("Transacciones plataforma error:", error);
    };

    const fechasInvalidas = ({ fecha_desde, fecha_hasta }) => {
        if (fecha_desde && fecha_hasta && fecha_desde > fecha_hasta) {
            showAlert("La fecha 'desde' no puede ser posterior a 'hasta'", "warning");
            return true;
        }
        return false;
    };

    // ---------- Reporte ----------
    const getResumen = async () => {
        cargandoResumen.value = true;
        try {
            await store.dispatch("appNegociosPlataforma/getResumenAction", limpiar(filtrosResumen.value));
        } catch (error) {
            handleError(error, "Error al cargar el reporte");
        } finally {
            cargandoResumen.value = false;
        }
    };

    const getInmobiliarias = async () => {
        try {
            await store.dispatch("appNegociosPlataforma/getInmobiliariasAction");
        } catch (error) {
            handleError(error, "Error al cargar las inmobiliarias");
        }
    };

    const aplicarFiltrosResumen = async () => {
        if (fechasInvalidas(filtrosResumen.value)) return;
        await getResumen();
    };

    const limpiarFiltrosResumen = () => {
        filtrosResumen.value = filtrosResumenVacios();
        nextTick(() => getResumen());
    };

    // ---------- Listado ----------
    const getNegocios = async (page = 1) => {
        if (page < 1) page = 1;
        spinner.value = true;
        listadoCargado = true;
        try {
            await store.dispatch("appNegociosPlataforma/getNegociosAction", {
                filters: limpiar(filtros.value),
                page,
            });
        } catch (error) {
            handleError(error, "Error al cargar las transacciones");
        } finally {
            spinner.value = false;
        }
    };

    const applyFilters = async () => {
        if (fechasInvalidas(filtros.value)) return;
        store.commit("appNegociosPlataforma/setCurrentPage", 1);
        await getNegocios(1);
    };

    const cleanFilter = () => {
        filtros.value = filtrosListadoVacios();
        store.commit("appNegociosPlataforma/setCurrentPage", 1);
        nextTick(() => getNegocios(1));
    };

    const pageChangeHandle = async (value) => {
        const currentPage = negocios.value.current_page || 1;
        let newPage;

        switch (value) {
            case "next":
                newPage = Math.min(currentPage + 1, negocios.value.last_page || 1);
                break;
            case "previous":
                newPage = Math.max(currentPage - 1, 1);
                break;
            default:
                newPage = parseInt(value) || 1;
                break;
        }

        store.commit("appNegociosPlataforma/setCurrentPage", newPage);
        await getNegocios(newPage);
    };

    const cambiarPestana = (nueva) => {
        pestana.value = nueva;
        if (nueva === "listado" && !listadoCargado) {
            getNegocios(1);
        }
    };

    // Desde el reporte: abre el listado de esa inmobiliaria
    const verTransaccionesDe = async (inmobiliariaId) => {
        filtros.value = { ...filtrosListadoVacios(), inmobiliaria_id: inmobiliariaId };
        pestana.value = "listado";
        await applyFilters();
    };

    // ---------- Detalle ----------
    let detallePeticion = 0;

    const verDetalle = async (fila) => {
        const peticion = ++detallePeticion;
        detalle.value = { ...fila };
        modalDetalle.value = true;
        cargandoDetalle.value = true;
        try {
            const respuesta = await store.dispatch("appNegociosPlataforma/getNegocioAction", fila.id);
            if (peticion === detallePeticion) detalle.value = respuesta;
        } catch (error) {
            if (peticion === detallePeticion) {
                modalDetalle.value = false;
                handleError(error, "Error al cargar el detalle");
            }
        } finally {
            if (peticion === detallePeticion) cargandoDetalle.value = false;
        }
    };

    const cerrarDetalle = () => {
        detallePeticion++;
        modalDetalle.value = false;
        cargandoDetalle.value = false;
        detalle.value = null;
    };

    // El enlace vence a los 5 minutos: se pide al hacer clic. La ventana se abre
    // antes de la petición para que el navegador no la bloquee como popup.
    const setDescargando = (valor) => {
        descargando.value = valor;
    };

    const descargarDocumento = async (documento) => {
        if (!detalle.value || descargando.value) return;
        setDescargando(documento.id);
        const ventana = window.open("", "_blank");
        try {
            const { url } = await store.dispatch("appNegociosPlataforma/getDescargaDocumentoAction", {
                negocioId: detalle.value.id,
                documentoId: documento.id,
            });
            (ventana || window).location.assign(url);
        } catch (error) {
            if (ventana) ventana.close();
            handleError(error, "Error al descargar el documento");
        } finally {
            setDescargando(null);
        }
    };

    const iniciar = async () => {
        await Promise.all([getInmobiliarias(), getResumen()]);
    };

    const headers = Object.freeze([
        { field: "id", label: "#" },
        { field: "inmobiliaria", label: "Inmobiliaria" },
        { field: "tipo", label: "Tipo" },
        { field: "inmueble", label: "Inmueble" },
        { field: "partes", label: "Propietario / Cliente" },
        { field: "valor", label: "Valor" },
        { field: "comision", label: "Comisión" },
        { field: "fecha_cierre", label: "Cierre" },
        { field: "estado", label: "Estado" },
        { field: "actions", label: "" },
    ]);

    const resumen = computed(() => store.state.appNegociosPlataforma.resumen);
    const inmobiliarias = computed(() => store.state.appNegociosPlataforma.inmobiliarias);
    const negocios = computed(() => store.state.appNegociosPlataforma.negocios);

    return {
        headers,
        pestana,
        sinAcceso,
        alertTable,
        resumen,
        inmobiliarias,
        negocios,
        filtrosResumen,
        cargandoResumen,
        filtros,
        spinner,
        modalDetalle,
        detalle,
        cargandoDetalle,
        descargando,
        iniciar,
        getResumen,
        aplicarFiltrosResumen,
        limpiarFiltrosResumen,
        getNegocios,
        applyFilters,
        cleanFilter,
        pageChangeHandle,
        cambiarPestana,
        verTransaccionesDe,
        verDetalle,
        cerrarDetalle,
        descargarDocumento,
    };
}
